import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { IdCard, Loader2 } from "lucide-react";
import { API, EVENTS, CLASSES, EVENT_DATE, DOB_MIN, DOB_MAX, ageOn, cleanMobile, validate, apiError } from "@/lib/reg";
import { Field, CheckRow } from "./Field";

const INIT = {
  full_name: "", father_name: "", mobile: "", parent_mobile: "", dob: "", player_class: "",
  school: "", events: [], emergency_contact: "", parent_consent: false, rules_agreement: false,
};

const AgeDisplay = ({ dob }) => {
  const a = ageOn(dob, EVENT_DATE);
  if (a === null) return <div className="age-box muted" data-testid="auto-age-display">जन्म तिथि चुनें · Select DOB</div>;
  const ok = dob >= DOB_MIN && dob <= DOB_MAX;
  return (
    <div className={`age-box ${ok ? "ok" : "bad"}`} data-testid="auto-age-display">
      <span className="text-2xl font-extrabold">{a}</span>
      <span className="text-xs leading-tight">वर्ष (1 नवंबर 2026 को)<br />years on event day</span>
    </div>
  );
};

export const RegistrationForm = ({ onSuccess }) => {
  const [f, setF] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const set = (k) => (v) => {
    setF((p) => ({ ...p, [k]: v }));
    setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  };
  const inp = (k) => ({
    id: k, name: k, value: f[k], onChange: (e) => set(k)(e.target.value),
    className: `gp-input ${errors[k] ? "bad" : ""}`, "data-testid": `input-${k.replace(/_/g, "-")}`,
  });
  const mob = (k) => ({ ...inp(k), type: "tel", inputMode: "numeric", maxLength: 14, placeholder: "98XXXXXXXX" });
  const toggleEvent = (id) =>
    set("events")(f.events.includes(id) ? f.events.filter((x) => x !== id) : [...f.events, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (lock.current) return;
    const errs = Object.fromEntries(Object.entries(validate(f)).filter(([, v]) => v));
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("कृपया लाल रंग में दिखी गलतियाँ ठीक करें / Please fix the highlighted errors");
      document.getElementById(Object.keys(errs)[0])?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      const body = { ...f, mobile: cleanMobile(f.mobile), parent_mobile: cleanMobile(f.parent_mobile), emergency_contact: cleanMobile(f.emergency_contact) };
      const { data } = await axios.post(`${API}/registrations`, body);
      toast.success("पंजीकरण सफल! Registration successful");
      onSuccess(data);
      setTimeout(() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <section id="register" className="gp-container pb-6 scroll-mt-20">
      <form onSubmit={submit} noValidate className="form-card" data-testid="registration-form">
        <div className="tricolour-bar rounded-t-2xl" />
        <div className="p-5 sm:p-8 space-y-5">
          <div>
            <p className="eyebrow">पंजीकरण फॉर्म · Registration Form</p>
            <h2 className="mt-1 text-2xl sm:text-3xl font-bold text-navy font-hi">खिलाड़ी पंजीकरण</h2>
            <p className="text-sm text-slate-500">सभी * चिह्नित जानकारी आवश्यक है · All * fields are required</p>
          </div>

          <div className="aadhaar-notice" data-testid="aadhaar-notice-alert">
            <IdCard className="shrink-0 text-amber-700" size={22} />
            <div className="text-sm">
              <p className="font-hi font-semibold text-amber-900">प्रतियोगिता के दिन आयु एवं पहचान सत्यापन के लिए अपना आधार कार्ड साथ लाना अनिवार्य है।</p>
              <p className="text-amber-800/80 mt-1">Participants must bring their Aadhaar card on event day for age and identity verification. We do not collect Aadhaar details online.</p>
            </div>
          </div>

          <Field label="खिलाड़ी का पूरा नाम" en="Player Full Name" name="full_name" error={errors.full_name}>
            <input {...inp("full_name")} autoComplete="name" placeholder="e.g. Rahul Kumar" maxLength={100} />
          </Field>

          <Field label="पिता का नाम" en="Father's Name" name="father_name" error={errors.father_name}>
            <input {...inp("father_name")} placeholder="e.g. Suresh Kumar" maxLength={100} />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="मोबाइल नंबर" en="Mobile Number" name="mobile" error={errors.mobile}>
              <input {...mob("mobile")} />
            </Field>
            <Field label="अभिभावक मोबाइल" en="Parent/Guardian Mobile" name="parent_mobile" error={errors.parent_mobile}>
              <input {...mob("parent_mobile")} />
            </Field>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <Field label="जन्म तिथि" en="Date of Birth" name="dob" error={errors.dob}>
              <input {...inp("dob")} type="date" min={DOB_MIN} max={DOB_MAX} />
            </Field>
            <div className="pb-[2px] self-start mt-[26px]"><AgeDisplay dob={f.dob} /></div>
          </div>

          <div className="grid sm:grid-cols-[180px_1fr] gap-5">
            <Field label="कक्षा" en="Class" name="player_class" error={errors.player_class}>
              <select {...inp("player_class")} data-testid="select-class">
                <option value="">कक्षा चुनें · Select</option>
                {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="स्कूल का नाम" en="School Name" name="school" error={errors.school}>
              <input {...inp("school")} placeholder="e.g. Govt. Sr. Sec. School, Manpur" maxLength={100} />
            </Field>
          </div>

          <Field label="खेल प्रतियोगिताएँ (एक से अधिक चुन सकते हैं)" en="Sports Events" name="events" error={errors.events}>
            <div id="events" className="grid grid-cols-2 gap-2">
              {EVENTS.map((ev) => {
                const on = f.events.includes(ev.id);
                return (
                  <button type="button" key={ev.id} onClick={() => toggleEvent(ev.id)} aria-pressed={on}
                    className={`ev-chip ${on ? "on" : ""}`} data-testid={`checkbox-event-${ev.id}`}>
                    <span className="font-hi font-semibold text-sm">{ev.hi}</span>
                    <span className="text-[11px] opacity-70">{ev.en}</span>
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="आपातकालीन संपर्क नंबर" en="Emergency Contact Number" name="emergency_contact" error={errors.emergency_contact}>
            <input {...mob("emergency_contact")} />
          </Field>

          <div className="space-y-3 pt-1">
            <CheckRow name="parent_consent" checked={f.parent_consent} onChange={set("parent_consent")} error={errors.parent_consent}>
              <b className="font-hi">अभिभावक सहमति *</b> — मैं (अभिभावक) अपने बच्चे को इस प्रतियोगिता में भाग लेने की अनुमति देता/देती हूँ। <span className="text-slate-500">I, the parent/guardian, consent to my child's participation.</span>
            </CheckRow>
            <CheckRow name="rules_agreement" checked={f.rules_agreement} onChange={set("rules_agreement")} error={errors.rules_agreement}>
              <b className="font-hi">खेल नियम *</b> — मैं आयोजकों और निर्णायकों के सभी खेल नियमों व निर्णयों का पालन करूँगा। <span className="text-slate-500">I agree to follow all sports rules and decisions of the organizers.</span>
            </CheckRow>
          </div>

          <button type="submit" disabled={busy} className="btn-saffron w-full justify-center text-base" data-testid="submit-registration-btn">
            {busy ? <><Loader2 className="animate-spin" size={18} /> जमा हो रहा है... Submitting</> : "पंजीकरण जमा करें · Submit Registration"}
          </button>
        </div>
      </form>
    </section>
  );
};
